import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { FormLayout } from "@/components/Forms";
import Button from "@/components/Button";
import FormField from "@/components/Forms/FormField";
import { ProductionSceneShoppingItemResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";

type FormDataT = {
  title: string;
  address?: string;
  date_time?: string;
  content: string;
  owner?: string;
  note?: string;
  production_scene_id: number;
};

interface Props {
  onClose: () => void;
  scenes: number[];
  shoppingItem?: ProductionSceneShoppingItemResource;
}

const ShoppingListModal: FC<Props> = ({ onClose, scenes, shoppingItem }) => {
  const { projectId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    clearErrors,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      title: shoppingItem?.title ?? "",
      address: shoppingItem?.address ?? "",
      date_time: shoppingItem?.time ?? "",
      note: shoppingItem?.note ?? "",
      owner: shoppingItem?.owner ?? "",
      content: shoppingItem?.content ?? "",
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        if (shoppingItem) {
          await api.put<never, { id: number }>(
            `/v1/production/scenes/shopping-items/${shoppingItem.id}`,
            data
          );
        } else {
          await api.post<never, ProductionSceneShoppingItemResource>(
            "/v1/production/scenes/shopping-items",
            data
          );
        }

        queryClient.invalidateQueries(["production_projects", { projectId }]);

        alerts.success(
          `Shopping item has been successfully ${
            !!shoppingItem ? "updated" : "created"
          }
          `
        );

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={`${!shoppingItem ? "Add" : "Edit"} item`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="title"
                label="Element"
                id="name"
              />
              {!shoppingItem && (
                <FormField<FormDataT, number>
                  control={control}
                  name="production_scene_id"
                  label="Scene number"
                  id="scene_id"
                  type="select"
                  options={scenes.map((scene, index) => {
                    return {
                      label: (index + 1).toString(),
                      value: scene,
                    };
                  })}
                />
              )}
              <FormField<FormDataT>
                control={control}
                name="address"
                label="Address"
                id="address"
                suggestionsEndpoint="/v1/production/scenes/shopping-items/locations"
              />
              <FormField<FormDataT>
                control={control}
                name="date_time"
                label="Start Date & Time"
                id="date"
                type="datepicker"
                datepickerType="datetime"
              />
              <FormField<FormDataT>
                control={control}
                name="content"
                label="Item Info"
                type="textarea"
                id="content"
              />
              <FormField<FormDataT>
                control={control}
                name="owner"
                label="Assignee"
                id="owner"
                suggestionsEndpoint="/v1/production/scenes/shopping-items/owners"
              />
              <FormField<FormDataT>
                control={control}
                name="note"
                label="Notes"
                type="textarea"
                id="notes"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default ShoppingListModal;
